import React, { useState, useRef, useEffect } from 'react';
import { Play } from 'lucide-react';

// Added #t=0.001 to the end - this is a "magic" trick for iPhones
// that forces the browser to load the first frame immediately.
const DEMO_VIDEO = "/ruma-demo.mp4#t=0.001";
const APP_SCREENSHOT = "/ruma-app-preview.jpg";

const VideoDemo = () => {
  const [showVideo, setShowVideo] = useState(false);
  const videoRef = useRef(null);

  const startVideo = () => {
    // 1. Show the video container
    setShowVideo(true);

    // 2. We use a tiny delay to ensure the element is visible 
    // before asking the iPhone to go fullscreen
    setTimeout(() => {
      const video = videoRef.current;
      if (video) {
        // Unmute and Play
        video.muted = false;
        
        // Trigger Native Fullscreen
        if (video.webkitEnterFullscreen) {
          video.webkitEnterFullscreen();
        }
        
        video.play().catch(err => {
          console.error("Playback failed:", err);
          // If it fails, showing controls is our backup
          video.controls = true;
        });
      }
    }, 100);
  };

  return (
    <section id="demo-video" className="py-24 md:py-32 relative overflow-hidden bg-black text-white">
      {/* Decorative Glow */}
      <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-[#00FFD1]/5 rounded-full blur-[150px] pointer-events-none" />
      
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-[#00FFD1] text-sm font-medium tracking-wider uppercase">See It In Action</span>
          <h2 className="text-3xl md:text-5xl font-bold mt-4">Watch the App Demo</h2>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <div className="relative bg-[#111111] border border-white/10 overflow-hidden rounded-sm shadow-2xl aspect-video group">
            
            {/* The Image Overlay (Only visible if video hasn't started) */}
            {!showVideo && (
              <div 
                className="absolute inset-0 z-20 cursor-pointer" 
                onClick={startVideo}
              >
                <img 
                  src={APP_SCREENSHOT} 
                  className="w-full h-full object-cover opacity-60 transition-opacity group-hover:opacity-40" 
                  alt="App Preview" 
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 bg-[#00FFD1] flex items-center justify-center rounded-sm shadow-2xl transform transition-transform group-hover:scale-110">
                    <Play className="w-10 h-10 text-black fill-current" />
                  </div>
                </div>
              </div>
            )}

            {/* The Video Tag (Always present, but hidden behind image until play) */}
            <video
              ref={videoRef}
              className={`w-full h-full object-contain ${showVideo ? 'opacity-100' : 'opacity-0'}`}
              preload="metadata"
              playsInline={false} // Setting this to false encourages fullscreen on iPhone
              controls={showVideo} // Only show controls once playing
            >
              <source src={DEMO_VIDEO} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>

          {/* Highlights (Quick Links) */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
            {['Route Mgmt', 'Invoicing', 'Search', 'Analytics'].map((label, i) => (
              <div 
                key={i} 
                className="bg-[#121212] border border-white/10 p-4 text-center hover:border-[#00FFD1]/40 transition-colors cursor-pointer rounded-sm"
                onClick={startVideo}
              >
                <p className="text-[#00FFD1] text-xs font-mono mb-1">Feature 0{i+1}</p>
                <p className="text-white/80 text-xs font-bold uppercase tracking-tight">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default VideoDemo;